import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import {
  Settings,
  Save,
  Loader2,
  CheckCircle,
  X,
  AlertCircle,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ChatbotConfig {
  id: string;
  system_prompt: string;
  scope_description: string;
  fallback_message: string;
  support_enabled: boolean;
  moderation_enabled: boolean;
  webhook_url: string | null;
  updated_at: string;
}

interface AdminChatbotConfigProps {
  noLayout?: boolean;
}

export function AdminChatbotConfig({ noLayout = false }: AdminChatbotConfigProps = {}) {
  const navigate = useNavigate();
  const [config, setConfig] = useState<ChatbotConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('chatbot_config')
        .select('*')
        .limit(1)
        .single();

      if (error) throw error;

      setConfig(data);

    } catch (err) {
      console.error('Error fetching config:', err);
      setError('Fout bij ophalen van configuratie');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await supabase
        .from('chatbot_config')
        .update({
          system_prompt: config.system_prompt,
          scope_description: config.scope_description,
          fallback_message: config.fallback_message,
          support_enabled: config.support_enabled,
          moderation_enabled: config.moderation_enabled,
          webhook_url: config.webhook_url,
        })
        .eq('id', config.id);

      if (error) throw error;

      setSuccess('Configuratie opgeslagen');
      fetchConfig();

    } catch (err) {
      console.error('Error saving config:', err);
      setError('Fout bij opslaan van configuratie');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field: keyof ChatbotConfig, value: any) => {
    if (!config) return;
    setConfig({ ...config, [field]: value });
  };

  const loadingContent = (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  const noConfigContent = (
    <div className="text-center py-12">
      <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
      <p className="text-gray-600">Geen configuratie gevonden</p>
    </div>
  );

  if (loading) {
    return noLayout ? loadingContent : <AdminLayout>{loadingContent}</AdminLayout>;
  }

  if (!config) {
    return noLayout ? noConfigContent : <AdminLayout>{noConfigContent}</AdminLayout>;
  }

  const content = (
    <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chatbot Configuratie</h1>
            <p className="text-gray-600 mt-1">Beheer de instellingen van de dibbi chatbot</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Opslaan...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Opslaan
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
            <X className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            {success}
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">AI Model Instellingen</h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Pas het AI model, temperature en max tokens aan voor chatbot antwoorden. Deze instellingen bepalen hoe creatief en uitgebreid de chatbot reageert.
                </p>
                <button
                  onClick={() => navigate('/admin/ai-settings')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Settings className="w-4 h-4" />
                  Open AI Settings
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-blue-200">
              <p className="text-xs text-gray-600">
                <span className="font-medium">Tip:</span> Zoek naar "Chatbot Antwoorden" in de AI Settings om de model configuratie aan te passen.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">System Prompt</h2>
            <p className="text-sm text-gray-600 mt-1">
              De basis instructies voor de chatbot over hoe deze zich moet gedragen
            </p>
          </div>

          <div className="p-6">
            <textarea
              value={config.system_prompt}
              onChange={(e) => updateConfig('system_prompt', e.target.value)}
              rows={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              placeholder="Je bent dibbi, een vriendelijke assistent..."
            />
            <p className="text-xs text-gray-500 mt-2">
              Dit is het basis karakter en gedrag van de chatbot. Wees specifiek en duidelijk.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Scope Beschrijving</h2>
            <p className="text-sm text-gray-600 mt-1">
              Wat kan de chatbot voor gebruikers doen? Dit wordt toegevoegd aan het system prompt.
            </p>
          </div>

          <div className="p-6">
            <textarea
              value={config.scope_description}
              onChange={(e) => updateConfig('scope_description', e.target.value)}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              placeholder="Ik kan je helpen met..."
            />
            <p className="text-xs text-gray-500 mt-2">
              Lijst van onderwerpen en features waar de chatbot mee kan helpen.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Standaard Antwoord (Fallback Message)</h2>
            <p className="text-sm text-gray-600 mt-1">
              Het bericht dat wordt getoond wanneer de chatbot geen relevante informatie kan vinden
            </p>
          </div>

          <div className="p-6">
            <textarea
              value={config.fallback_message}
              onChange={(e) => updateConfig('fallback_message', e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              placeholder="Sorry, ik kan geen relevante informatie vinden..."
            />
            <p className="text-xs text-gray-500 mt-2">
              Dit bericht wordt getoond wanneer er geen matches zijn in de kennisbank.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Instellingen</h2>
            <p className="text-sm text-gray-600 mt-1">
              Configureer extra functionaliteit voor de chatbot
            </p>
          </div>

          <div className="p-6 space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.support_enabled}
                onChange={(e) => updateConfig('support_enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Support Tickets Ingeschakeld</span>
                <p className="text-xs text-gray-500">Gebruikers kunnen support tickets aanmaken via de chatbot</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.moderation_enabled}
                onChange={(e) => updateConfig('moderation_enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Content Moderatie Ingeschakeld</span>
                <p className="text-xs text-gray-500">Gebruik OpenAI Moderation API om ongepaste berichten te blokkeren</p>
              </div>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Webhook URL (optioneel)
              </label>
              <input
                type="url"
                value={config.webhook_url || ''}
                onChange={(e) => updateConfig('webhook_url', e.target.value || null)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="https://example.com/webhook"
              />
              <p className="text-xs text-gray-500 mt-1">
                Webhook die wordt aangeroepen wanneer een support ticket wordt aangemaakt
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-medium text-gray-900 mb-2">Laatst bijgewerkt</h2>
          <p className="text-sm text-gray-600">
            {new Date(config.updated_at).toLocaleString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        </div>
      </div>
  );

  if (noLayout) {
    return content;
  }

  return <AdminLayout>{content}</AdminLayout>;
}
